/**
 * Reset Password Page — /auth/reset-password
 *
 * The user arrives here from the Supabase password-reset email.
 * Because supabaseClient.ts is configured with detectSessionInUrl: true and
 * flowType: 'pkce', the client automatically exchanges the tokens in the URL
 * fragment and establishes a PASSWORD_RECOVERY session before this page renders.
 *
 * The new password is saved with the sign-in the link itself made, never with
 * whatever account the browser holds when the person presses Save. Every tab of
 * the app shares one stored sign-in: on 2026-11-03 another tab signed in as a
 * different account refreshed it three seconds after the link opened, and
 * supabase.auth.updateUser() set the new password on that other account. The
 * page now keeps the link's access token and user, names the account on the
 * form, and saves through the sign-in service with that token. Then we redirect
 * to /auth/login.
 */

import { useState, useEffect, useRef } from 'react';
import type { Session } from '@supabase/supabase-js';
import { useNavigate, Link } from 'react-router-dom';
import { KeyRound, Loader2, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { setPasswordForRecovery, type RecoverySignIn } from '../../lib/passwordRecovery';

type PageState = 'form' | 'submitting' | 'success' | 'invalid';

function passwordStrengthError(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters.';
  return null;
}

export default function ResetPassword() {
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>('form');
  const [sessionReady, setSessionReady] = useState(false);
  // The link's own sign-in. A PASSWORD_RECOVERY event always wins; any other
  // session is taken only if nothing has been captured yet (the first one the
  // client has after reading the link).
  const recoveryRef = useRef<RecoverySignIn | null>(null);
  const [recoveryEmail, setRecoveryEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Wait for Supabase to exchange the URL tokens and establish a recovery session.
  // onAuthStateChange fires with event 'PASSWORD_RECOVERY' when ready.
  useEffect(() => {
    const supabase = getSupabaseClient();

    // If there are no tokens in the URL at all, mark as invalid immediately.
    const hash = window.location.hash;
    const hasTokens = hash.includes('access_token') || hash.includes('type=recovery');

    if (!hasTokens) {
      // Could be a pkce flow where tokens arrive as query params
      const params = new URLSearchParams(window.location.search);
      if (!params.has('code') && !params.has('token_hash')) {
        setPageState('invalid');
        return;
      }
    }

    // Listen for the PASSWORD_RECOVERY event (hash fragment flow) or just
    // check if there is already a session (pkce flow resolves it before mount).
    const capture = (session: Session | null, fromRecovery: boolean) => {
      if (!session?.access_token) return;
      if (recoveryRef.current && !fromRecovery) return;
      recoveryRef.current = { accessToken: session.access_token, email: session.user?.email ?? null };
      setRecoveryEmail(recoveryRef.current.email);
      setSessionReady(true);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') capture(session, true);
      else if (event === 'SIGNED_IN') capture(session, false);
    });

    // Also check for an existing session right away (pkce may already be resolved).
    supabase.auth.getSession().then(({ data: { session } }) => capture(session, false));

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const strengthError = passwordStrengthError(password);
    if (strengthError) {
      setError(strengthError);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    const recovery = recoveryRef.current;
    if (!recovery) {
      setError('The reset link has not signed you in yet. Open the link from the email again.');
      return;
    }

    setPageState('submitting');
    try {
      const updateErr = await setPasswordForRecovery(recovery, password);
      if (updateErr) {
        setError(updateErr);
        setPageState('form');
        return;
      }
      setPageState('success');
      // Sign this tab out so the person starts fresh at login with the new password.
      await getSupabaseClient().auth.signOut({ scope: 'local' });
      setTimeout(() => navigate('/auth/login', { replace: true }), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setPageState('form');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface)] dark:bg-[var(--bg)] px-4 transition-colors duration-300">
      <div className="w-full max-w-sm">
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-sm dark:shadow-none p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-consulting-slate-blue-100 dark:bg-[var(--active-tint)]">
              <KeyRound className="h-6 w-6 text-consulting-slate-blue-600 dark:text-[var(--text-ter)]" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Set a new password
              </h1>
            </div>
          </div>

          {/* Invalid / expired link */}
          {pageState === 'invalid' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 text-sm text-[var(--coral)] dark:text-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)]/50 p-4">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>
                  This link is invalid or has expired. Password reset links are single-use and
                  expire after one hour.
                </span>
              </div>
              <Link
                to="/auth/forgot-password"
                className="block w-full text-center py-3 bg-consulting-slate-blue-600 text-white font-medium hover:bg-consulting-slate-blue-700 transition-colors"
              >
                Request a new link
              </Link>
            </div>
          )}

          {/* Success */}
          {pageState === 'success' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)]/50 p-4">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>Password updated{recoveryEmail ? ` for ${recoveryEmail}` : ''}. Redirecting to sign in…</span>
              </div>
            </div>
          )}

          {/* Form */}
          {(pageState === 'form' || pageState === 'submitting') && pageState !== 'invalid' && (
            <>
              {!sessionReady && (
                <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4">
                  Verifying your reset link…
                </p>
              )}
              {recoveryEmail && (
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-4" data-testid="reset-account">
                  New password for <strong>{recoveryEmail}</strong>
                </p>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1"
                  >
                    New password
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      className="no-dark flex-1 px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:ring-2 focus:ring-consulting-slate-blue-500 dark:focus:ring-[var(--active-tint)] focus:border-consulting-slate-blue-500 dark:focus:border-[var(--slate)] outline-none transition-all"
                      disabled={pageState === 'submitting' || !sessionReady}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="p-2 text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule-bright)] dark:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="confirm"
                    className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1"
                  >
                    Confirm password
                  </label>
                  <input
                    id="confirm"
                    type={showPassword ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat your password"
                    autoComplete="new-password"
                    className="no-dark w-full px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:ring-2 focus:ring-consulting-slate-blue-500 dark:focus:ring-[var(--active-tint)] focus:border-consulting-slate-blue-500 dark:focus:border-[var(--slate)] outline-none transition-all"
                    disabled={pageState === 'submitting' || !sessionReady}
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-sm text-[var(--coral)] dark:text-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)]/50 p-3">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={pageState === 'submitting' || !sessionReady}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-consulting-slate-blue-600 text-white font-medium hover:bg-consulting-slate-blue-700 disabled:opacity-50 transition-colors"
                >
                  {pageState === 'submitting' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    'Set new password'
                  )}
                </button>

                <Link
                  to="/auth/login"
                  className="block text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
                >
                  Back to sign in
                </Link>
              </form>
            </>
          )}
        </div>

        <div className="mt-4 flex justify-center">
          <ThemeToggle variant="labeled" />
        </div>
      </div>
    </div>
  );
}
